import { Stack } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import crmConnectorsApi from "../../../api/crmConnectorsApi";
import useClientContext from "../../../context/useClientContext";
import IntAppLogo from "../../../images/intapp.png";
import IntegrationPageContainer from "../integration/IntegrationPageContainer";
import CrmActions from "./CrmActions";
import { CrmConnectorContextProvider } from "./CrmConnectorContextProvider";
import CrmManagement from "./CrmManagement";
import CrmSetupInfo from "./CrmSetupInfo";
import FullResyncInfo from "./FullResyncInfo";
import TestConnectionInfo from "./TestConnectionInfo";
import type { FullResyncState, TestConnectionState } from "./crmConnectorState";
import { getInitialFullResyncState, getInitialTestConnectionState } from "./crmConnectorState";

const promotionText =
  "DealCloud is a comprehensive deal and relationship management platform developed by IntApp. It is designed to help financial and professional services firms streamline their deal processes, manage relationships, and make data-driven decisions.";

const DealCloudConnectorPage = () => {
  usePageTitle("DealCloud");

  const { dealCloudApp } = useClientContext();

  const [connectionInfo, fetchError] = useFetch(crmConnectorsApi.dealcloud.getConnection);

  const [testConnectionState, setTestConnectionState] = useState<TestConnectionState>(() =>
    getInitialTestConnectionState()
  );
  const [fullResyncState, setFullResyncState] = useState<FullResyncState>(getInitialFullResyncState);

  if (fetchError) {
    logError(fetchError, "[DealCloudConnectorPage] getConnection");
    return <DataLoadingFailed title="Loading connector settings failed" text={fetchError.message} />;
  }

  const loading = connectionInfo === undefined;
  if (loading) {
    return <InlineLoader />;
  }

  return (
    <CrmConnectorContextProvider
      crmName="DealCloud"
      crmApiName="dealcloud"
      logoSrc={IntAppLogo}
      connectionInfo={connectionInfo}
      syncEnabled={false}
      testConnectionState={testConnectionState}
      onTestConnectionStateUpdate={setTestConnectionState}
      fullResyncState={fullResyncState}
      onFullResyncStateUpdate={setFullResyncState}
    >
      <IntegrationPageContainer
        title="DealCloud"
        logoSrc={IntAppLogo}
        overviewText={promotionText}
        appStatus={dealCloudApp?.status}
        maxWidth="xl"
      >
        <CrmSetupInfo />
        <CrmActions hideTestConnection hideDelete hideDataSyncToggle />
        <Stack spacing={2} sx={{ width: "48rem" }}>
          <TestConnectionInfo />
          <FullResyncInfo />
        </Stack>
        <CrmManagement hideConnectionManagement excludeLogsColumns={["contacts", "investors"]} />
      </IntegrationPageContainer>
    </CrmConnectorContextProvider>
  );
};

export default DealCloudConnectorPage;
